// React
import { ReactElement } from 'react';
// Next
import { useTranslation } from 'next-i18next';
// Context
import { useDashboard } from '@utmb/contexts/DashboardContext';
import { toast } from 'react-toastify';
// Modules
import { useFormik } from 'formik';
// Interfaces
import { DashboardModalFormProps } from '@utmb/types/dashboard-modal';
import UserMemberUpdateStatusView from '@utmb/types/view/UserMemberUpdateStatusView';
import { UserMemberUpdateStatusEnum } from '@utmb/types/enum/UserMemberUpdateStatusEnum';
// Design System
import { Checkbox } from '@utmb/design-system/checkbox/Checkbox';
// Features
import { ActionsButtons } from '@utmb/features/user-edit-wrapper/ActionsButtons';
import { getKeycloakToken } from '@utmb/features/sso/keycloak';
// Utils
import { FetchMethodsEnum, fetchTheAPI } from '@utmb/utils/utmb';
// Types
import { DashboardMemberPreferenceCenterView } from '@utmb/types/index';
// Styles
import styles from './user-account-settings-form.module.scss';

export const UserAccountSettingsForm = (props: DashboardModalFormProps): ReactElement | null => {
    const { onCancel } = props;

    const { t } = useTranslation();
    const { dashboardMember, updateDashboardMemberPreferenceCenter } = useDashboard();

    const { optInWs, optInLive, optInIndex } = dashboardMember?.preferenceCenter || ({} as DashboardMemberPreferenceCenterView);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            optInWs: Boolean(optInWs),
            optInLive: Boolean(optInLive),
            optInIndex: Boolean(optInIndex),
        },
        onSubmit: async (values) => {
            const token = await getKeycloakToken();

            const response = (await fetchTheAPI({
                isClient: true,
                path: '/users/dashboard/member/preference-center',
                bearer: token,
                method: FetchMethodsEnum.PUT,
                body: values,
            })) as UserMemberUpdateStatusView;

            if (response.statusCode === UserMemberUpdateStatusEnum.OK) {
                toast.success(t('myutmb:myUtmb.myInformation.successMessage'));
                updateDashboardMemberPreferenceCenter(values as DashboardMemberPreferenceCenterView);
                onCancel();
            } else {
                toast.error(t('myutmb:myUtmb.myInformation.errorMessage'));
            }
        },
    });

    if (!dashboardMember) {
        return null;
    }

    return (
        <form onSubmit={formik.handleSubmit} className={styles.form}>
            <div className={styles.input_container}>
                <Checkbox
                    id="optInWs"
                    name="optInWs"
                    checked={formik.values.optInWs}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label={t('myutmb:myUtmb.myInformation.accountSettings.optInWs')}
                />
                <Checkbox
                    id="optInLive"
                    name="optInLive"
                    checked={formik.values.optInLive}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label={t('myutmb:myUtmb.myInformation.accountSettings.optInLive')}
                />
                <Checkbox
                    id="optInIndex"
                    name="optInIndex"
                    checked={formik.values.optInIndex}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label={t('myutmb:myUtmb.myInformation.accountSettings.optInIndex')}
                />
            </div>

            <ActionsButtons onCancel={onCancel} />
        </form>
    );
};
